import { UpdateAnnexIVPayload } from '../services/AnnexIVService';
import { FieldValue } from '../types/DocumentationFieldValue';
import {
  AnnexIVDesignAndDevelopment,
  AnnexIVLifecycleRiskPostMarketAssurance,
  AnnexIVSystemOverview,
  CreateAnnexIVDraftResponse
} from '../types/Documentation.types';
import {
  CreateEUDeclarationResponse,
  EUDeclarationConformityStatement,
  EUDeclarationContent,
  EUDeclarationHarmonisedStandard,
  EUDeclarationPersonalDataCompliance,
  UpdateEUDeclarationPayload
} from '../types/EUDeclaration.types';
import {
  CreateFRIAResponse,
  UpdateFRIAPayload,
  FRIARiskScenario
} from '../types/FRIA.types';
import {
  CONFORMITY_STATEMENTS_KEY,
  HARMONISED_STANDARDS_KEY,
  PERSONAL_DATA_COMPLIANCE_KEY,
  PROVIDER_DECLARATION_CHECKED_KEY
} from '../components/features/Draft/DeclarationOfConformity/DeclarationOfConformity';
import {
  RISK_SCENARIOS_KEY,
  VULNERABLE_GROUPS_KEY,
  ROLE_OF_SYSTEM_KEY,
  FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY,
  OVERSIGHT_MODEL_KEY,
  OVERALL_RISK_MITIGATION_STATUS_KEY,
  OVERALL_DECISION_KEY
} from '../components/features/Draft/FRIA/FRIA.consts';
import {
  CreateDPIAResponse,
  DPIAUpdatePayload,
  Levels
} from '../types/DPIA.types';

type AnnexSectionKey =
  | 'systemOverview'
  | 'designAndDevelopment'
  | 'lifecycleRiskPostMarketAssurance';

const ANNEX_IV_SECTION_LABELS: Record<AnnexSectionKey, string> = {
  systemOverview: 'System Overview',
  designAndDevelopment: 'Design & Development',
  lifecycleRiskPostMarketAssurance: 'Lifecycle, Risk & Post-Market Assurance'
};

const ANNEX_IV_FIELD_LABELS: Record<AnnexSectionKey, Record<string, string>> = {
  systemOverview: {
    intendedPurpose: 'Intended purpose & context of use',
    deploymentScope: 'Deployment scope and target population',
    formsInWhichTheSystemIsPlaced:
      'Forms in which the system is placed / put into service',
    hardware: 'Hardware / runtime environment',
    componentOfAProduct: 'If component of a product',
    userInterfaceOverview: 'User interface overview',
    instructionsForUse: 'Instructions for Use (IFU)'
  },
  designAndDevelopment: {
    developmentMethodsAndLifecycle: 'Development methods & lifecycle',
    designSpecifications: 'Design specifications',
    systemArchitectureAndCompute: 'System architecture & compute',
    dataRequirementsAndDatasets: 'Data requirements & datasets',
    humanOversightMeasures: 'Human oversight measures',
    predeterminedChangesEnvelope: 'Pre‑determined changes (PDM) envelope',
    validationAndTesting: 'Validation & testing',
    cybersecurityMeasures: 'Cybersecurity measures'
  },
  lifecycleRiskPostMarketAssurance: {
    performanceMetrics: 'Performance Metrics',
    riskManagementSystem: 'Risk Management System',
    lifecycleChanges: 'Lifecycle Changes',
    standardsAndSpecifications: 'Standards & Specifications',
    euDeclarationOfConformity: 'EU Declaration of Conformity',
    postMarketPerformanceAndMonitoringPlan:
      'Post‑Market Performance & Monitoring Plan'
  }
};

const ANNEX_IV_LABEL_METADATA: Record<
  string,
  { section: AnnexSectionKey; backendKey: string }
> = Object.entries(ANNEX_IV_FIELD_LABELS).reduce(
  (acc, [sectionKey, fields]) => {
    Object.entries(fields).forEach(([backendKey, label]) => {
      acc[label] = { section: sectionKey as AnnexSectionKey, backendKey };
    });
    return acc;
  },
  {} as Record<string, { section: AnnexSectionKey; backendKey: string }>
);

const createEmptySystemOverview = (): AnnexIVSystemOverview => ({
  intendedPurpose: null,
  deploymentScope: null,
  formsInWhichTheSystemIsPlaced: null,
  hardware: null,
  componentOfAProduct: null,
  userInterfaceOverview: null,
  instructionsForUse: null
});

const createEmptyDesignAndDevelopment = (): AnnexIVDesignAndDevelopment => ({
  developmentMethodsAndLifecycle: null,
  designSpecifications: null,
  systemArchitectureAndCompute: null,
  dataRequirementsAndDatasets: null,
  humanOversightMeasures: null,
  predeterminedChangesEnvelope: null,
  validationAndTesting: null,
  cybersecurityMeasures: null
});

const createEmptyLifecycle = (): AnnexIVLifecycleRiskPostMarketAssurance => ({
  performanceMetrics: null,
  riskManagementSystem: null,
  lifecycleChanges: null,
  standardsAndSpecifications: null,
  euDeclarationOfConformity: null,
  postMarketPerformanceAndMonitoringPlan: null
});

export const mapAnnexContentToFields = (
  content?: Partial<CreateAnnexIVDraftResponse>
): Record<string, FieldValue> => {
  if (!content) {
    return {};
  }

  const result: Record<string, FieldValue> = {};

  (Object.keys(ANNEX_IV_FIELD_LABELS) as AnnexSectionKey[]).forEach(
    (sectionKey) => {
      const sectionValues = content[sectionKey];
      if (!sectionValues) return;

      Object.entries(ANNEX_IV_FIELD_LABELS[sectionKey]).forEach(
        ([backendKey, label]) => {
          const value = (sectionValues as Record<string, string | null>)[
            backendKey
          ];
          if (value) {
            result[label] = {
              label,
              value,
              section: ANNEX_IV_SECTION_LABELS[sectionKey]
            };
          }
        }
      );
    }
  );

  return result;
};

export const buildAnnexIVPayload = (
  fields: Record<string, FieldValue>,
  baseContent?: Partial<CreateAnnexIVDraftResponse>
): UpdateAnnexIVPayload => {
  const payload: UpdateAnnexIVPayload = {
    systemOverview: {
      ...createEmptySystemOverview(),
      ...(baseContent?.systemOverview ?? {})
    },
    designAndDevelopment: {
      ...createEmptyDesignAndDevelopment(),
      ...(baseContent?.designAndDevelopment ?? {})
    },
    lifecycleRiskPostMarketAssurance: {
      ...createEmptyLifecycle(),
      ...(baseContent?.lifecycleRiskPostMarketAssurance ?? {})
    }
  };

  Object.values(fields).forEach((field) => {
    const metadata = ANNEX_IV_LABEL_METADATA[field.label];
    if (!metadata) return;

    const sectionTitle = ANNEX_IV_SECTION_LABELS[metadata.section];
    if (sectionTitle !== field.section) {
      return;
    }

    (payload[metadata.section] as Record<string, string | null>)[
      metadata.backendKey
    ] = field.value;
  });

  return payload;
};

type EUSectionKey =
  | 'systemProviderInformation'
  | 'conformityStatements'
  | 'personalDataCompliance'
  | 'harmonisedStandards'
  | 'notifiedBody'
  | 'footerNote';

const EU_SECTION_LABELS: Record<EUSectionKey, string> = {
  systemProviderInformation: 'System and Provider Information',
  conformityStatements: 'Conformity Statement',
  personalDataCompliance: 'Personal Data Compliance',
  harmonisedStandards: 'Harmonised Standards / Common Specifications',
  notifiedBody: 'Notified Body & Conformity Assessment',
  footerNote: 'Footer Note'
};

const EU_FIELD_LABELS: Partial<Record<EUSectionKey, Record<string, string>>> = {
  systemProviderInformation: {
    aiSystemTypeCategory: 'AI System type / category',
    uniqueReferenceTraceabilityId: 'Unique reference / traceability ID',
    intendedPurposeContentOfUse: 'Intended purpose & context of use',
    registeredTradeNameTrademark: 'Registered trade name / trademark (if any)',
    registeredAddress: 'Registered address (street, city, country)',
    contact: 'Contact',
    authorisedRepresentativeName:
      'Authorised representative in the EU (if applicable)',
    authorisedRepresentativeAddress: 'Authorised representative address'
  },
  notifiedBody: {
    notifiedBodyName: 'Notified body name (if notified body involved)',
    notifiedBodyIdentificationNumber:
      'Notified body identification number (if notified body involved)',
    conformityAssessmentProcedure: 'Conformity assessment procedure performed',
    certificateIdentification: 'Certificate identification'
  }
};

const EU_LABEL_METADATA: Record<
  string,
  { section: EUSectionKey; backendKey: string }
> = Object.entries(EU_FIELD_LABELS).reduce(
  (acc, [sectionKey, fields]) => {
    if (!fields) return acc;
    Object.entries(fields).forEach(([backendKey, label]) => {
      acc[label] = { section: sectionKey as EUSectionKey, backendKey };
    });
    return acc;
  },
  {} as Record<string, { section: EUSectionKey; backendKey: string }>
);

export const mapEUDeclarationContentToFields = (
  content?: Partial<EUDeclarationContent>
): Record<string, FieldValue> => {
  if (!content) {
    return {};
  }

  const result: Record<string, FieldValue> = {};

  Object.entries(EU_FIELD_LABELS).forEach(([sectionKey, fields]) => {
    if (!fields) return;
    const sectionValues = (content as Record<string, unknown>)[sectionKey];
    if (!sectionValues) return;

    Object.entries(fields).forEach(([backendKey, label]) => {
      const value = (sectionValues as Record<string, string | undefined>)[
        backendKey
      ];
      if (value) {
        result[label] = {
          label,
          value,
          section: EU_SECTION_LABELS[sectionKey as EUSectionKey]
        };
      }
    });
  });

  // Initialize conformityStatements array
  if (content.conformityStatements?.conformityStatements) {
    result[CONFORMITY_STATEMENTS_KEY] = {
      label: CONFORMITY_STATEMENTS_KEY,
      value: JSON.stringify(content.conformityStatements.conformityStatements),
      section: 'Conformity Statement'
    };
  }

  // Initialize harmonisedStandards array
  if (content.harmonisedStandards?.harmonisedStandards) {
    result[HARMONISED_STANDARDS_KEY] = {
      label: HARMONISED_STANDARDS_KEY,
      value: JSON.stringify(content.harmonisedStandards.harmonisedStandards),
      section: 'Harmonised Standards / Common Specifications'
    };
  }

  // Initialize personalDataCompliance object
  if (content.personalDataCompliance) {
    // Use the actual values from content, don't override with defaults
    const compliance = {
      text: content.personalDataCompliance.text,
      complied: content.personalDataCompliance.complied // Use actual value (true/false/undefined)
    };
    result[PERSONAL_DATA_COMPLIANCE_KEY] = {
      label: PERSONAL_DATA_COMPLIANCE_KEY,
      value: JSON.stringify(compliance),
      section: 'Personal Data Compliance'
    };
  }

  // Initialize declarationStatement object (Provider Declaration checkbox)
  if (content.declarationStatement) {
    // Use the actual values from content
    const declarationStatement = {
      text: content.declarationStatement.text,
      complied: content.declarationStatement.complied // Use actual value (true/false/undefined)
    };
    // Store the complied value as a string in Redux
    result[PROVIDER_DECLARATION_CHECKED_KEY] = {
      label: PROVIDER_DECLARATION_CHECKED_KEY,
      value: (declarationStatement.complied ?? false).toString(),
      section: 'Declaration'
    };
  }

  return result;
};

export const buildEUDeclarationPayload = (
  fields: Record<string, FieldValue>,
  baseContent?: CreateEUDeclarationResponse | null
): UpdateEUDeclarationPayload | null => {
  if (!baseContent?.documentControl) {
    return null;
  }

  const seededFields = baseContent
    ? {
        ...mapEUDeclarationContentToFields(baseContent),
        ...fields
      }
    : fields;
  const payload: UpdateEUDeclarationPayload = {
    content: {
      documentControl: { ...baseContent.documentControl },
      systemProviderInformation: {
        ...(baseContent.systemProviderInformation ?? {})
      },
      // Don't initialize declarationStatement here - it will be set later based on field value
      declarationStatement: baseContent.declarationStatement ?? {
        text: undefined,
        complied: undefined
      },
      conformityStatements: {
        ...(baseContent.conformityStatements ?? {}),
        conformityStatements:
          baseContent.conformityStatements?.conformityStatements ?? []
      },
      // Don't initialize personalDataCompliance here - it will be set later based on field value
      personalDataCompliance: baseContent.personalDataCompliance ?? {
        text: undefined,
        complied: undefined
      },
      harmonisedStandards: {
        ...(baseContent.harmonisedStandards ?? {}),
        harmonisedStandards:
          baseContent.harmonisedStandards?.harmonisedStandards ?? []
      },
      notifiedBody: {
        ...(baseContent.notifiedBody ?? {})
      },
      footerNote: {
        ...(baseContent.footerNote ?? { text: '', generatedBy: '' })
      }
    }
  };

  // Process regular fields
  Object.values(seededFields).forEach(({ label, value, section }) => {
    // Skip special keys - they're handled separately
    if (
      label === CONFORMITY_STATEMENTS_KEY ||
      label === HARMONISED_STANDARDS_KEY ||
      label === PERSONAL_DATA_COMPLIANCE_KEY ||
      label === PROVIDER_DECLARATION_CHECKED_KEY
    ) {
      return;
    }

    const metadata = EU_LABEL_METADATA[label];
    if (!metadata) return;

    const expectedSectionLabel = EU_SECTION_LABELS[metadata.section];
    if (expectedSectionLabel !== section) {
      return;
    }

    const target = payload.content[metadata.section] as Record<string, any>;
    target[metadata.backendKey] = value;
  });

  // Handle conformityStatements array - always ensure the array property exists
  const conformityStatementsField = seededFields[CONFORMITY_STATEMENTS_KEY];
  let conformityStatementsArray: EUDeclarationConformityStatement[] = [];

  if (
    conformityStatementsField !== undefined &&
    conformityStatementsField.value
  ) {
    // User has interacted with the field and has data
    try {
      conformityStatementsArray = JSON.parse(
        conformityStatementsField.value
      ) as EUDeclarationConformityStatement[];
    } catch (error) {
      console.error('Failed to parse conformityStatements:', error);
      // Fallback to baseContent or empty array
      conformityStatementsArray =
        baseContent.conformityStatements?.conformityStatements ?? [];
    }
  } else if (conformityStatementsField !== undefined) {
    // User has interacted but cleared all data - use empty array
    conformityStatementsArray = [];
  } else {
    // User hasn't interacted - use baseContent or empty array
    conformityStatementsArray =
      baseContent.conformityStatements?.conformityStatements ?? [];
  }

  // Always set the array property
  payload.content.conformityStatements = {
    ...(baseContent.conformityStatements ?? {}),
    conformityStatements: conformityStatementsArray
  };

  // Handle harmonisedStandards array - always ensure the array property exists
  const harmonisedStandardsField = seededFields[HARMONISED_STANDARDS_KEY];
  let harmonisedStandardsArray: EUDeclarationHarmonisedStandard[] = [];

  if (
    harmonisedStandardsField !== undefined &&
    harmonisedStandardsField.value
  ) {
    // User has interacted with the field and has data
    try {
      harmonisedStandardsArray = JSON.parse(
        harmonisedStandardsField.value
      ) as EUDeclarationHarmonisedStandard[];
    } catch (error) {
      console.error('Failed to parse harmonisedStandards:', error);
      // Fallback to baseContent or empty array
      harmonisedStandardsArray =
        baseContent.harmonisedStandards?.harmonisedStandards ?? [];
    }
  } else if (harmonisedStandardsField !== undefined) {
    // User has interacted but cleared all data - use empty array
    harmonisedStandardsArray = [];
  } else {
    // User hasn't interacted - use baseContent or empty array
    harmonisedStandardsArray =
      baseContent.harmonisedStandards?.harmonisedStandards ?? [];
  }

  // Always set the array property
  payload.content.harmonisedStandards = {
    ...(baseContent.harmonisedStandards ?? {}),
    harmonisedStandards: harmonisedStandardsArray
  };

  // Handle personalDataCompliance object - INDEPENDENT from declarationStatement
  // This comes from the Personal Data Compliance radio button group
  const personalDataComplianceField =
    seededFields[PERSONAL_DATA_COMPLIANCE_KEY];

  if (personalDataComplianceField !== undefined) {
    // Field exists - user has interacted with radio button, so always use the field value
    if (personalDataComplianceField.value) {
      try {
        const compliance = JSON.parse(
          personalDataComplianceField.value
        ) as EUDeclarationPersonalDataCompliance;
        // Always override with the field value - use the actual complied value from the radio button
        payload.content.personalDataCompliance = {
          text: compliance.text, // Text from radio button option
          complied: compliance.complied // Complied value from radio button (true/false based on selected option)
        };
      } catch (error) {
        console.error('Failed to parse personalDataCompliance:', error);
        // On parse error, use baseContent but ensure complied is set
        payload.content.personalDataCompliance = {
          ...(baseContent.personalDataCompliance ?? {}),
          complied: baseContent.personalDataCompliance?.complied ?? false
        };
      }
    } else {
      // User has interacted but cleared the value - set to default
      payload.content.personalDataCompliance = {
        text: undefined,
        complied: false
      };
    }
  } else {
    // Field doesn't exist - use baseContent value as-is (user hasn't interacted with radio button)
    if (baseContent.personalDataCompliance) {
      payload.content.personalDataCompliance = {
        ...baseContent.personalDataCompliance
      };
    }
  }

  // Handle declarationStatement object - INDEPENDENT from personalDataCompliance
  // This comes from the Provider Declaration checkbox
  const declarationStatementField =
    seededFields[PROVIDER_DECLARATION_CHECKED_KEY];
  const declarationText =
    'We, the undersigned provider, hereby declare under our sole responsibility that the AI system identified above fulfils the requirements listed below.';

  if (declarationStatementField !== undefined) {
    // Field exists - user has interacted with checkbox, so always use the field value
    if (declarationStatementField.value) {
      const isChecked = declarationStatementField.value === 'true';
      // Set declarationStatement based on checkbox state - completely independent from personalDataCompliance
      // Always send the text, regardless of checkbox state
      payload.content.declarationStatement = {
        text: declarationText, // Always send the declaration text
        complied: isChecked // Complied value from checkbox (true if checked, false if not)
      };
    } else {
      // User has interacted but cleared the value - set to default
      payload.content.declarationStatement = {
        text: declarationText, // Always send the declaration text
        complied: false
      };
    }
  } else {
    // Field doesn't exist - use baseContent value as-is (user hasn't interacted with checkbox)
    if (baseContent.declarationStatement) {
      payload.content.declarationStatement = {
        text: baseContent.declarationStatement.text || declarationText, // Use baseContent text if exists, otherwise use default
        complied: baseContent.declarationStatement.complied ?? false
      };
    } else {
      // Initialize with default values if baseContent doesn't have declarationStatement
      payload.content.declarationStatement = {
        text: declarationText, // Always send the declaration text
        complied: false
      };
    }
  }

  return payload;
};

export const mergeFieldRecords = (
  current: Record<string, FieldValue>,
  updates: Record<string, FieldValue>
): Record<string, FieldValue> => ({
  ...current,
  ...updates
});

export const EU_SECTION_TITLES = EU_SECTION_LABELS;
export const ANNEX_SECTION_TITLES = ANNEX_IV_SECTION_LABELS;

export const isFilledValue = (value?: string) => {
  if (!value) return false;

  const stripped = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

  return stripped.length > 0;
};

export const hasDynamicGroupContent = (
  jsonValue: string | undefined
): boolean => {
  if (!jsonValue) return false;

  const rows = JSON.parse(jsonValue) as Record<string, unknown>[];

  return rows.some((row) =>
    Object.values(row).some((value) =>
      isFilledValue(typeof value === 'string' ? value : String(value ?? ''))
    )
  );
};

// FRIA Field Mappings
const FRIA_FIELD_MAPPINGS: Record<string, { section: string; path: string[] }> =
  {
    // FRIA Meta Information
    friaDate: {
      section: 'FRIA Meta Information',
      path: ['friaMetaInformation', 'friaDate']
    },
    dateOfApproval: {
      section: 'FRIA Meta Information',
      path: ['friaMetaInformation', 'dateOfApproval']
    },
    preparedBy: {
      section: 'FRIA Meta Information',
      path: ['friaMetaInformation', 'preparedBy']
    },
    reviewedApprovedBy: {
      section: 'FRIA Meta Information',
      path: ['friaMetaInformation', 'reviewedApprovedBy']
    },

    // System Deployer Information
    organisationName: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'organisationName']
    },
    address: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'address']
    },
    contactPerson: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'contactPerson']
    },
    roleTitle: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'roleTitle']
    },
    email: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'email']
    },
    systemName: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'systemName']
    },
    providerName: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'providerName']
    },
    versionModelID: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'versionModelID']
    },
    highRiskCategory: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'highRiskCategory']
    },
    internalSystemID: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'internalSystemID']
    },
    linkToProviders: {
      section: 'System Deployer Information',
      path: ['systemDeployerInformation', 'linkToProviders']
    },

    // Use Context
    businessProcess: {
      section: 'Use Context',
      path: ['useContext', 'businessProcess']
    },
    purposeOfDeploying: {
      section: 'Use Context',
      path: ['useContext', 'purposeOfDeploying']
    },
    humanJudgement: {
      section: 'Use Context',
      path: ['useContext', 'humanJudgement']
    },

    // Duration & Frequency of Use
    expectedStartDateOfUse: {
      section: 'Duration & Frequency of Use',
      path: ['durationFrequencyOfUse', 'expectedStartDateOfUse']
    },
    expectedDurationOfUse: {
      section: 'Duration & Frequency of Use',
      path: ['durationFrequencyOfUse', 'expectedDurationOfUse']
    },
    frequencyOfUse: {
      section: 'Duration & Frequency of Use',
      path: ['durationFrequencyOfUse', 'frequencyOfUse']
    },
    estimatedNumberOfIndividualsAffected: {
      section: 'Duration & Frequency of Use',
      path: ['durationFrequencyOfUse', 'estimatedNumberOfIndividualsAffected']
    },
    descriptionInteractionMoments: {
      section: 'Duration & Frequency of Use',
      path: ['durationFrequencyOfUse', 'descriptionInteractionMoments']
    },

    // Affected Persons & Groups
    primaryAffectedIndividuals: {
      section: 'Affected Persons & Groups',
      path: ['affectedPersonsGroups', 'primaryAffectedIndividuals']
    },
    indirectlyAffectedPersonsGroups: {
      section: 'Affected Persons & Groups',
      path: ['affectedPersonsGroups', 'indirectlyAffectedPersonsGroups']
    },
    humanJudgementForVulnerableGroups: {
      section: 'Affected Persons & Groups',
      path: ['affectedPersonsGroups', 'humanJudgement']
    },

    // Fundamental Rights Risks
    protectionOfVulnerableGroups: {
      section: 'Fundamental Rights Risks',
      path: [
        'fundamentalRightsRisks',
        'fundamentalRightsInScope',
        'protectionOfVulnerableGroups'
      ]
    },

    // Human Oversight
    oversightRolesResponsibilities: {
      section: 'Human Oversight',
      path: ['humanOversight', 'oversightRolesResponsibilities']
    },
    oversightProcedures: {
      section: 'Human Oversight',
      path: ['humanOversight', 'oversightProcedures']
    },
    training: {
      section: 'Human Oversight',
      path: ['humanOversight', 'training']
    },

    // Mitigation & Corrective Measures
    dataControls: {
      section: 'Mitigation & Corrective Measures',
      path: ['mitigationCorrectiveMeasures', 'dataControls']
    },
    processOrganizationalControls: {
      section: 'Mitigation & Corrective Measures',
      path: ['mitigationCorrectiveMeasures', 'processOrganizationalControls']
    },
    transparencyCommunication: {
      section: 'Mitigation & Corrective Measures',
      path: ['mitigationCorrectiveMeasures', 'transparencyCommunication']
    },
    incidentHandlingSuspensionRules: {
      section: 'Mitigation & Corrective Measures',
      path: ['mitigationCorrectiveMeasures', 'incidentHandlingSuspensionRules']
    },
    complaintRedressMechanisms: {
      section: 'Mitigation & Corrective Measures',
      path: ['mitigationCorrectiveMeasures', 'complaintRedressMechanisms']
    },

    // Final Assessment Decision
    additionalMitigation: {
      section: 'Final Assessment Decision',
      path: ['finalAssessmentDecision', 'additionalMitigation']
    },
    conditionsForDeployment: {
      section: 'Final Assessment Decision',
      path: ['finalAssessmentDecision', 'conditionsForDeployment']
    }
  };

// Helper to get nested value from object
const getNestedValue = (obj: any, path: string[]): any => {
  return path.reduce((current, key) => current?.[key], obj);
};

export const mapFRIAContentToFields = (
  content?: Partial<CreateFRIAResponse>
): Record<string, FieldValue> => {
  if (!content) {
    return {};
  }

  const result: Record<string, FieldValue> = {};

  // Map simple fields
  Object.entries(FRIA_FIELD_MAPPINGS).forEach(([key, { section, path }]) => {
    const value = getNestedValue(content, path);
    if (value !== undefined && value !== null && value !== '') {
      result[key] = {
        label: key,
        value: String(value),
        section
      };
    }
  });

  // Map role of system (checkboxes)
  if (content.useContext?.roleOfSystem) {
    result[ROLE_OF_SYSTEM_KEY] = {
      label: ROLE_OF_SYSTEM_KEY,
      value: JSON.stringify(content.useContext.roleOfSystem),
      section: 'Use Context'
    };
  }

  // Map vulnerable groups (checkboxes)
  if (content.affectedPersonsGroups?.vulnerableGroups) {
    result[VULNERABLE_GROUPS_KEY] = {
      label: VULNERABLE_GROUPS_KEY,
      value: JSON.stringify(content.affectedPersonsGroups.vulnerableGroups),
      section: 'Affected Persons & Groups'
    };
  }

  // Map fundamental rights in scope (checkboxes)
  if (content.fundamentalRightsRisks?.fundamentalRightsInScope) {
    const { protectionOfVulnerableGroups, ...booleanFields } =
      content.fundamentalRightsRisks.fundamentalRightsInScope;
    result[FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY] = {
      label: FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY,
      value: JSON.stringify(booleanFields),
      section: 'Fundamental Rights Risks'
    };
  }

  // Map risk scenarios (dynamic field group)
  if (content.fundamentalRightsRisks?.scenarios) {
    result[RISK_SCENARIOS_KEY] = {
      label: RISK_SCENARIOS_KEY,
      value: JSON.stringify(content.fundamentalRightsRisks.scenarios),
      section: 'Fundamental Rights Risks'
    };
  }

  // Map oversight model (checkboxes)
  if (content.humanOversight?.oversightModel) {
    result[OVERSIGHT_MODEL_KEY] = {
      label: OVERSIGHT_MODEL_KEY,
      value: JSON.stringify(content.humanOversight.oversightModel),
      section: 'Human Oversight'
    };
  }

  // Map overall risk mitigation status (radio buttons)
  if (content.finalAssessmentDecision?.overallRiskMitigationStatus) {
    const status = content.finalAssessmentDecision.overallRiskMitigationStatus;
    const selectedKey =
      Object.entries(status).find(([_, value]) => value === true)?.[0] || '';
    result[OVERALL_RISK_MITIGATION_STATUS_KEY] = {
      label: OVERALL_RISK_MITIGATION_STATUS_KEY,
      value: selectedKey,
      section: 'Final Assessment Decision'
    };
  }

  // Map overall decision (radio buttons)
  if (content.overallDecision) {
    const selectedKey =
      Object.entries(content.overallDecision).find(
        ([_, value]) => value === true
      )?.[0] || '';
    result[OVERALL_DECISION_KEY] = {
      label: OVERALL_DECISION_KEY,
      value: selectedKey,
      section: 'Overall Decision'
    };
  }

  return result;
};

// Helper to set nested value in object
const setNestedValue = (
  obj: Record<string, any>,
  path: string[],
  value: any
): void => {
  let current = obj;
  for (let i = 0; i < path.length - 1; i++) {
    if (!(path[i] in current)) {
      current[path[i]] = {};
    }
    current = current[path[i]];
  }
  current[path[path.length - 1]] = value;
};

export const buildFRIAPayload = (
  fields: Record<string, FieldValue>,
  baseContent?: CreateFRIAResponse | null
): UpdateFRIAPayload => {
  const payload: UpdateFRIAPayload = {
    friaMetaInformation: {
      ...(baseContent?.friaMetaInformation ?? {})
    },
    systemDeployerInformation: {
      ...(baseContent?.systemDeployerInformation ?? {})
    },
    useContext: {
      ...(baseContent?.useContext ?? {})
    },
    durationFrequencyOfUse: {
      ...(baseContent?.durationFrequencyOfUse ?? {})
    },
    affectedPersonsGroups: {
      ...(baseContent?.affectedPersonsGroups ?? {})
    },
    fundamentalRightsRisks: {
      fundamentalRightsInScope: {
        ...(baseContent?.fundamentalRightsRisks?.fundamentalRightsInScope ?? {})
      },
      scenarios: baseContent?.fundamentalRightsRisks?.scenarios ?? []
    },
    humanOversight: {
      ...(baseContent?.humanOversight ?? {})
    },
    mitigationCorrectiveMeasures: {
      ...(baseContent?.mitigationCorrectiveMeasures ?? {})
    },
    finalAssessmentDecision: {
      ...(baseContent?.finalAssessmentDecision ?? {})
    },
    overallDecision: {
      ...(baseContent?.overallDecision ?? {})
    }
  };

  // Process simple fields from FRIA_FIELD_MAPPINGS
  Object.entries(fields).forEach(({ 0: key, 1: field }) => {
    // Skip special keys - they're handled separately
    if (
      key === RISK_SCENARIOS_KEY ||
      key === VULNERABLE_GROUPS_KEY ||
      key === ROLE_OF_SYSTEM_KEY ||
      key === FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY ||
      key === OVERSIGHT_MODEL_KEY ||
      key === OVERALL_RISK_MITIGATION_STATUS_KEY ||
      key === OVERALL_DECISION_KEY
    ) {
      return;
    }

    const mapping = FRIA_FIELD_MAPPINGS[key];
    if (!mapping) return;

    setNestedValue(payload as Record<string, any>, mapping.path, field.value);
  });

  // Handle role of system (checkboxes)
  const roleOfSystemField = fields[ROLE_OF_SYSTEM_KEY];
  if (roleOfSystemField?.value) {
    try {
      payload.useContext!.roleOfSystem = JSON.parse(roleOfSystemField.value);
    } catch (error) {
      console.error('Failed to parse roleOfSystem:', error);
    }
  }

  // Handle vulnerable groups (checkboxes)
  const vulnerableGroupsField = fields[VULNERABLE_GROUPS_KEY];
  if (vulnerableGroupsField?.value) {
    try {
      payload.affectedPersonsGroups!.vulnerableGroups = JSON.parse(
        vulnerableGroupsField.value
      );
    } catch (error) {
      console.error('Failed to parse vulnerableGroups:', error);
    }
  }

  // Handle fundamental rights in scope (checkboxes)
  const fundamentalRightsField = fields[FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY];
  if (fundamentalRightsField?.value) {
    try {
      const booleanFields = JSON.parse(fundamentalRightsField.value);
      payload.fundamentalRightsRisks!.fundamentalRightsInScope = {
        ...payload.fundamentalRightsRisks!.fundamentalRightsInScope,
        ...booleanFields
      };
    } catch (error) {
      console.error('Failed to parse fundamentalRightsInScope:', error);
    }
  }

  // Handle risk scenarios (dynamic field group)
  const riskScenariosField = fields[RISK_SCENARIOS_KEY];
  if (riskScenariosField?.value) {
    try {
      payload.fundamentalRightsRisks!.scenarios = JSON.parse(
        riskScenariosField.value
      ) as FRIARiskScenario[];
    } catch (error) {
      console.error('Failed to parse riskScenarios:', error);
    }
  }

  // Handle oversight model (checkboxes)
  const oversightModelField = fields[OVERSIGHT_MODEL_KEY];
  if (oversightModelField?.value) {
    try {
      payload.humanOversight!.oversightModel = JSON.parse(
        oversightModelField.value
      );
    } catch (error) {
      console.error('Failed to parse oversightModel:', error);
    }
  }

  // Handle overall risk mitigation status (radio button)
  const riskMitigationStatusField = fields[OVERALL_RISK_MITIGATION_STATUS_KEY];
  if (riskMitigationStatusField?.value) {
    payload.finalAssessmentDecision!.overallRiskMitigationStatus = {
      identifiedRisksAreMitigated:
        riskMitigationStatusField.value === 'identifiedRisksAreMitigated',
      identifiedRisksAreNotYetAcceptable:
        riskMitigationStatusField.value ===
        'identifiedRisksAreNotYetAcceptable',
      risksArePartiallyMitigated:
        riskMitigationStatusField.value === 'risksArePartiallyMitigated'
    };
  }

  // Handle overall decision (radio button)
  const overallDecisionField = fields[OVERALL_DECISION_KEY];
  if (overallDecisionField?.value) {
    payload.overallDecision = {
      approve: overallDecisionField.value === 'approve',
      approveWithConditions:
        overallDecisionField.value === 'approveWithConditions',
      postponeDoNotDeploy: overallDecisionField.value === 'postponeDoNotDeploy'
    };
  }

  return payload;
};

// Add these constants for the special keys
const DPIA_OVERALL_ASSESSMENT_KEY = '__overallAssessment__';
const DPIA_FINAL_DECISION_KEY = '__finalDecision__';
const RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY = '__riskAssessment__';
const MEASURES_TO_ADDRESS_RISKS_KEY = '__measuresToAddressRisks__';

// Helper function to get the selected key from a levels object
const getSelectedLevelKey = (levels: {
  low: boolean;
  medium: boolean;
  high: boolean;
}): string => {
  return Object.entries(levels).find(([_, value]) => value === true)?.[0] || '';
};

// DPIA Field Mappings
const DPIA_FIELD_MAPPINGS: Record<string, { section: string; path: string[] }> =
  {
    // Controller and Assessment Identification
    organizationName: {
      section: 'Controller and Assessment Identification',
      path: ['controllerAndAssessmentIdentification', 'organizationName']
    },
    address: {
      section: 'Controller and Assessment Identification',
      path: ['controllerAndAssessmentIdentification', 'address']
    },
    contactPerson: {
      section: 'Controller and Assessment Identification',
      path: ['controllerAndAssessmentIdentification', 'contactPerson']
    },
    role: {
      section: 'Controller and Assessment Identification',
      path: ['controllerAndAssessmentIdentification', 'role']
    },
    email: {
      section: 'Controller and Assessment Identification',
      path: ['controllerAndAssessmentIdentification', 'email']
    },

    // DPO / Data Protection Contact
    dpoContactPerson: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'contactPerson'
      ]
    },
    dpoEmail: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'email'
      ]
    },
    dpiaTitle: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'dpiaTitle'
      ]
    },
    referenceId: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'referenceId'
      ]
    },
    dpiaDate: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'dpiaDate'
      ]
    },
    dateOfApproval: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'dateOfApproval'
      ]
    },
    preparedBy: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'preparedBy'
      ]
    },
    reviewedBy: {
      section: 'DPO / Data Protection Contact (if applicable)',
      path: [
        'controllerAndAssessmentIdentification',
        'dpoDataProtectionContact',
        'reviewedBy'
      ]
    },

    // Description of Processing Operations & Purposes
    processingOverview: {
      section: 'Description of Processing Operations & Purposes',
      path: [
        'descriptionOfProcessingOperationsAndPurposes',
        'processingOverview'
      ]
    },
    dataFlowsAndRecipients: {
      section: 'Description of Processing Operations & Purposes',
      path: [
        'descriptionOfProcessingOperationsAndPurposes',
        'dataFlowsAndRecipients'
      ]
    },
    legalBasisForProcessing: {
      section: 'Description of Processing Operations & Purposes',
      path: [
        'descriptionOfProcessingOperationsAndPurposes',
        'legalBasisForProcessing'
      ]
    },
    categoriesOfDataSubjects: {
      section: 'Description of Processing Operations & Purposes',
      path: [
        'descriptionOfProcessingOperationsAndPurposes',
        'categoriesOfDataSubjects'
      ]
    },
    categoriesOfPersonalData: {
      section: 'Description of Processing Operations & Purposes',
      path: [
        'descriptionOfProcessingOperationsAndPurposes',
        'categoriesOfPersonalData'
      ]
    },

    // Necessity and Proportionality
    necessityOfProcessing: {
      section: 'Necessity and Proportionality',
      path: ['necessityAndProportionality', 'necessityOfProcessing']
    },
    dataMinimization: {
      section: 'Necessity and Proportionality',
      path: ['necessityAndProportionality', 'dataMinimization']
    },
    storageLimitation: {
      section: 'Necessity and Proportionality',
      path: ['necessityAndProportionality', 'storageLimitation']
    },
    transparencyAndInformationToDataSubjects: {
      section: 'Necessity and Proportionality',
      path: [
        'necessityAndProportionality',
        'transparencyAndInformationToDataSubjects'
      ]
    },
    dataSubjectsRights: {
      section: 'Necessity and Proportionality',
      path: ['necessityAndProportionality', 'dataSubjectsRights']
    },
    processorsAndJointControllers: {
      section: 'Necessity and Proportionality',
      path: ['necessityAndProportionality', 'processorsAndJointControllers']
    },

    // Consultation and Prior Consultation
    internalConsultation: {
      section: 'Consultation and Prior Consultation',
      path: ['consultationAndPriorConsultation', 'internalConsultation']
    },
    dpoConsultation: {
      section: 'Consultation and Prior Consultation',
      path: ['consultationAndPriorConsultation', 'dpoConsultation']
    },
    consultationWithDataSubjectsOrRepresentatives: {
      section: 'Consultation and Prior Consultation',
      path: [
        'consultationAndPriorConsultation',
        'consultationWithDataSubjectsOrRepresentatives'
      ]
    },
    priorConsultationWithSupervisoryAuthority: {
      section: 'Consultation and Prior Consultation',
      path: [
        'consultationAndPriorConsultation',
        'priorConsultationWithSupervisoryAuthority'
      ]
    }
  };

export const mapDPIAContentToFields = (
  content?: Partial<CreateDPIAResponse>
): Record<string, FieldValue> => {
  if (!content) {
    return {};
  }

  const result: Record<string, FieldValue> = {};

  // Map simple fields using the centralized mapping
  Object.entries(DPIA_FIELD_MAPPINGS).forEach(([key, { section, path }]) => {
    const value = getNestedValue(content, path);

    if (value !== undefined && value !== null && value !== '') {
      result[key] = {
        label: key,
        value: String(value),
        section
      };
    }
  });

  // Handle overallAssessmentOfResidualRisk (radio buttons)
  if (content.finalConclusionsAndApproval?.overallAssessmentOfResidualRisk) {
    const assessment =
      content.finalConclusionsAndApproval.overallAssessmentOfResidualRisk;
    const selectedKey =
      Object.entries(assessment).find(([_, value]) => value === true)?.[0] ||
      '';
    result[DPIA_OVERALL_ASSESSMENT_KEY] = {
      label: DPIA_OVERALL_ASSESSMENT_KEY,
      value: selectedKey,
      section: 'Final Conclusions and Approval'
    };
  }

  // Handle finalDecisionOnProcessing (radio buttons)
  if (content.finalConclusionsAndApproval?.finalDecisionOnProcessing) {
    const decision =
      content.finalConclusionsAndApproval.finalDecisionOnProcessing;
    const selectedKey =
      Object.entries(decision).find(([_, value]) => value === true)?.[0] || '';
    result[DPIA_FINAL_DECISION_KEY] = {
      label: DPIA_FINAL_DECISION_KEY,
      value: selectedKey,
      section: 'Final Conclusions and Approval'
    };
  }

  // Handle risk assessment array
  if (
    content.riskAssessmentRightsAndFreedomsOfDataSubjects
      ?.rightsAndFreedomsOfDataSubjects
  ) {
    const risks =
      content.riskAssessmentRightsAndFreedomsOfDataSubjects
        .rightsAndFreedomsOfDataSubjects;
    const mappedRisks = risks.map((risk) => ({
      riskID: risk.riskID || '',
      riskDescription: risk.riskDescription || '',
      affectedDataSubjects: risk.affectedDataSubjects || '',
      likelihoodOfOccurrence: getSelectedLevelKey(
        risk.likelihoodOfOccurrence || {
          low: false,
          medium: false,
          high: false
        }
      ),
      severity: getSelectedLevelKey(
        risk.severity || { low: false, medium: false, high: false }
      ),
      initialRiskLevel: getSelectedLevelKey(
        risk.initialRiskLevel || { low: false, medium: false, high: false }
      ),
      existingControls: risk.existingControls || ''
    }));

    result[RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY] = {
      label: RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY,
      value: JSON.stringify(mappedRisks),
      section: 'Risk Assessment for Rights and Freedoms of Data Subjects'
    };
  }

  // Handle measures to address risks array
  if (content.measuresToAddressRisks?.measuresToAddressRisks) {
    const measures = content.measuresToAddressRisks.measuresToAddressRisks;
    const mappedMeasures = measures.map((measure) => ({
      riskID: measure.riskID || '',
      mitigationMeasures: measure.mitigationMeasures
        ? Array.isArray(measure.mitigationMeasures)
          ? measure.mitigationMeasures.join(', ')
          : measure.mitigationMeasures
        : '',
      type: measure.type || '',
      ownerOrResponsibleRole: measure.ownerOrResponsibleRole || '',
      implementationTimeline: measure.implementationTimeline || '',
      residualRisk: getSelectedLevelKey(
        measure.residualRisk || { low: false, medium: false, high: false }
      )
    }));

    result[MEASURES_TO_ADDRESS_RISKS_KEY] = {
      label: MEASURES_TO_ADDRESS_RISKS_KEY,
      value: JSON.stringify(mappedMeasures),
      section: 'Measures to Address Risks'
    };
  }

  return result;
};

// Add missing constants at the top with other DPIA constants
const DPIA_CATEGORIES_OF_DATA_SUBJECTS_KEY = '__categoriesOfDataSubjects__';
const DPIA_CATEGORIES_OF_PERSONAL_DATA_KEY = '__categoriesOfPersonalData__';
const DPIA_RIGHTS_AND_FREEDOMS_RISKS_KEY = '__rightsAndFreedomsRisks__';
const DPIA_RESIDUAL_RISK_ACCEPTABLE_KEY = '__residualRiskAcceptable__';

// Helper function to create levels object
const createLevelsFromString = (level: string): Levels => ({
  low: level === 'low',
  medium: level === 'medium',
  high: level === 'high'
});

const normalizeSectionName = (section: string): string =>
  section
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, ' ')
    .trim();

export const buildDPIAPayload = (
  fields: Record<string, FieldValue>,
  baseContent?: CreateDPIAResponse | null
): DPIAUpdatePayload | null => {
  if (!baseContent) {
    return null;
  }

  const payload: DPIAUpdatePayload = {
    controllerAndAssessmentIdentification: {
      organizationName:
        baseContent.controllerAndAssessmentIdentification?.organizationName ||
        '',
      address: baseContent.controllerAndAssessmentIdentification?.address || '',
      contactPerson:
        baseContent.controllerAndAssessmentIdentification?.contactPerson || '',
      role: baseContent.controllerAndAssessmentIdentification?.role || '',
      email: baseContent.controllerAndAssessmentIdentification?.email || '',
      dpoDataProtectionContact: {
        contactPerson:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.contactPerson || '',
        email:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.email || '',
        dpiaTitle:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.dpiaTitle || '',
        referenceId:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.referenceId || '',
        dpiaDate:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.dpiaDate || '',
        dateOfApproval:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.dateOfApproval || '',
        preparedBy:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.preparedBy || '',
        reviewedBy:
          baseContent.controllerAndAssessmentIdentification
            ?.dpoDataProtectionContact?.reviewedBy || ''
      }
    },
    descriptionOfProcessingOperationsAndPurposes: {
      ...baseContent.descriptionOfProcessingOperationsAndPurposes
    },
    necessityAndProportionality: {
      ...baseContent.necessityAndProportionality
    },
    riskAssessmentRightsAndFreedomsOfDataSubjects: {
      rightsAndFreedomsOfDataSubjects:
        baseContent.riskAssessmentRightsAndFreedomsOfDataSubjects
          ?.rightsAndFreedomsOfDataSubjects || []
    },
    measuresToAddressRisks: {
      measuresToAddressRisks:
        baseContent.measuresToAddressRisks?.measuresToAddressRisks || []
    },
    consultationAndPriorConsultation: {
      ...baseContent.consultationAndPriorConsultation
    },
    finalConclusionsAndApproval: {
      overallAssessmentOfResidualRisk: {
        residualRiskAcceptable: false,
        residualRiskNonAcceptable: false,
        ...baseContent.finalConclusionsAndApproval
          ?.overallAssessmentOfResidualRisk
      },
      finalDecisionOnProcessing: {
        proceedWithProcessingAsPlanned: false,
        proceedWithProcessingSubjectToConditions: false,
        postponeProcessing: false,
        ...baseContent.finalConclusionsAndApproval?.finalDecisionOnProcessing
      }
    }
  };

  // Process simple fields
  Object.values(fields).forEach(({ label, value, section }) => {
    // Skip special array/boolean fields
    if (
      label === RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY ||
      label === MEASURES_TO_ADDRESS_RISKS_KEY ||
      label === DPIA_OVERALL_ASSESSMENT_KEY ||
      label === DPIA_FINAL_DECISION_KEY
    ) {
      return;
    }

    const mapping = DPIA_FIELD_MAPPINGS[label];
    if (!mapping) return;

    const isSectionMatch =
      normalizeSectionName(mapping.section) === normalizeSectionName(section);
    if (!isSectionMatch) return;

    // Navigate to the correct object in payload
    let target = payload as any;
    for (let i = 0; i < mapping.path.length - 1; i++) {
      const key = mapping.path[i];
      if (!target[key]) {
        target[key] = {};
      }
      target = target[key];
    }

    // Set the final value with validation
    const finalKey = mapping.path[mapping.path.length - 1];
    if (finalKey === 'email') {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && emailRegex.test(value)) {
        target[finalKey] = value;
      } else if (value) {
        console.warn(`Invalid email format for ${label}: ${value}`);
        return;
      } else {
        target[finalKey] = '';
      }
    } else {
      target[finalKey] = value;
    }
  });

  // Handle risk assessment array
  const riskAssessmentField =
    fields[RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY];
  if (riskAssessmentField?.value) {
    try {
      const risks = JSON.parse(riskAssessmentField.value) as any[];
      const mappedRisks = risks.map((risk) => ({
        riskID: risk.riskID || '',
        riskDescription: risk.riskDescription || '',
        affectedDataSubjects: risk.affectedDataSubjects || '',
        likelihoodOfOccurrence:
          typeof risk.likelihoodOfOccurrence === 'string'
            ? createLevelsFromString(risk.likelihoodOfOccurrence)
            : risk.likelihoodOfOccurrence || {
                low: false,
                medium: false,
                high: false
              },
        severity:
          typeof risk.severity === 'string'
            ? createLevelsFromString(risk.severity)
            : risk.severity || { low: false, medium: false, high: false },
        initialRiskLevel:
          typeof risk.initialRiskLevel === 'string'
            ? createLevelsFromString(risk.initialRiskLevel)
            : risk.initialRiskLevel || {
                low: false,
                medium: false,
                high: false
              },
        existingControls: risk.existingControls || ''
      }));

      payload.riskAssessmentRightsAndFreedomsOfDataSubjects!.rightsAndFreedomsOfDataSubjects =
        mappedRisks;
    } catch (error) {
      console.error('Failed to parse risk assessment:', error);
    }
  }

  // Handle measures to address risks array
  const measuresField = fields[MEASURES_TO_ADDRESS_RISKS_KEY];
  if (measuresField?.value) {
    try {
      const measures = JSON.parse(measuresField.value) as any[];
      const mappedMeasures = measures.map((measure) => ({
        riskID: measure.riskID || '',
        mitigationMeasures:
          typeof measure.mitigationMeasures === 'string'
            ? measure.mitigationMeasures
            : Array.isArray(measure.mitigationMeasures)
              ? measure.mitigationMeasures.join(', ')
              : '',
        type: measure.type || '',
        ownerOrResponsibleRole: measure.ownerOrResponsibleRole || '',
        implementationTimeline: measure.implementationTimeline || '',
        residualRisk:
          typeof measure.residualRisk === 'string'
            ? createLevelsFromString(measure.residualRisk)
            : measure.residualRisk || { low: false, medium: false, high: false }
      }));

      payload.measuresToAddressRisks!.measuresToAddressRisks = mappedMeasures;
    } catch (error) {
      console.error('Failed to parse measures to address risks:', error);
    }
  }

  // Handle overall assessment of residual risk (radio button)
  const overallAssessmentField = fields[DPIA_OVERALL_ASSESSMENT_KEY];
  if (overallAssessmentField?.value) {
    payload.finalConclusionsAndApproval!.overallAssessmentOfResidualRisk = {
      residualRiskAcceptable:
        overallAssessmentField.value === 'residualRiskAcceptable',
      residualRiskNonAcceptable:
        overallAssessmentField.value === 'residualRiskNonAcceptable'
    };
  }

  // Handle final decision on processing (radio button)
  const finalDecisionField = fields[DPIA_FINAL_DECISION_KEY];
  if (finalDecisionField?.value) {
    payload.finalConclusionsAndApproval!.finalDecisionOnProcessing = {
      proceedWithProcessingAsPlanned:
        finalDecisionField.value === 'proceedWithProcessingAsPlanned',
      proceedWithProcessingSubjectToConditions:
        finalDecisionField.value === 'proceedWithProcessingSubjectToConditions',
      postponeProcessing: finalDecisionField.value === 'postponeProcessing'
    };
  }

  return payload;
};

export {
  DPIA_OVERALL_ASSESSMENT_KEY,
  DPIA_FINAL_DECISION_KEY,
  RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY,
  MEASURES_TO_ADDRESS_RISKS_KEY,
  DPIA_CATEGORIES_OF_DATA_SUBJECTS_KEY,
  DPIA_CATEGORIES_OF_PERSONAL_DATA_KEY,
  DPIA_RIGHTS_AND_FREEDOMS_RISKS_KEY,
  DPIA_RESIDUAL_RISK_ACCEPTABLE_KEY
};
